import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import { COOKIE_IDS, dropDateOffset, pickupDateOffset } from '../Constants/Constants';
import { getCookie } from './CookieUtils';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

const commonDateFormat = (
  date: any,
  withoutTime = false,
  withoutYear = false,
  timezone?: string,
) => {
  let _date;
  if (date && timezone && dayjs(date)?.tz(timezone)?.isValid()) {
    _date = dayjs(date)
      ?.tz(timezone)
      ?.format(
        `ddd, MMM DD, ${!withoutYear ? 'YYYY' : ''} ${!withoutTime ? getTimeFormatAccordingToPreference(true) : ''}`,
      );
  } else {
    _date = dayjs(date).format(
      `ddd, MMM DD, ${!withoutYear ? 'YYYY' : ''} ${!withoutTime ? getTimeFormatAccordingToPreference(true) : ''}`,
    );
  }
  if (!date || _date === 'Invalid Date') return null;
  return _date;
};
// export const getTimeFormatAccordingToPreference = (withAMPM = false, withHH = false) => {
//   return getCookie(COOKIE_IDS?.PREFERRED_TIME_FORMAT) === '24HRS FORMAT'
//     ? 'HH:mm'
//     : `${withHH ? 'HH' : 'h'}:mm${withAMPM ? ' A' : ''}`;
// };

const convertToTimezoneFormat = (date: any, timezone: string): string | null => {
  if (!date || !timezone) return null;
  const formattedDate = dayjs(date)
    .tz(timezone)
    .format(`YYYY-MM-DDT${getTimeFormatAccordingToPreference()}:ss[Z]`);
  return formattedDate;
};

// Specifically for NATNAT EDIT as dayjs woll not be able to recognize 'h' as a proper time
const convertToTimezoneFormatwithSmallhh = (date: any, timezone: string): string | null => {
  if (!date || !timezone) return null;
  const formattedDate = dayjs(date)
    .tz(timezone)
    .format(`YYYY-MM-DDT${getTimeFormatAccordingToPreference(false, false, false, true)}:ss[Z]`);
  return formattedDate;
};
const appointmentDateFormat = (from: any, to: any): string | null => {
  if (!from || !to) return null;
  const formattedFromDate = dayjs(from)?.format(
    `ddd, MMM DD, ${getTimeFormatAccordingToPreference(true)}`,
  );
  return formattedFromDate;
};
const appointmentTimeFormat = (from: any, to: any): string | null => {
  if (!from || !to) return null;
  const formattedFromTime = dayjs(from)?.format(getTimeFormatAccordingToPreference(true));
  const formattedToTime = dayjs(to)?.format(getTimeFormatAccordingToPreference(true));
  return formattedFromTime + ' - ' + formattedToTime;
};
const isBeforeCurrentDate = (dateString: string): boolean => {
  const inputDate = dayjs(dateString, 'YYYY-MM-DD');
  if (!inputDate.isValid()) {
    return false;
  }
  const currentDate = dayjs().startOf('day');
  return inputDate.isBefore(currentDate);
};

const isBeforeCurrentDateAndTime = (date: any, timezone: any) => {
  if (!date || !timezone) return false;
  const inputDate = dayjs(date);
  if (!inputDate.isValid()) {
    return false;
  }
  const currentDate = dayjs().tz(timezone);
  return inputDate.isBefore(currentDate);
};
const isBeforeFirstDate = (earlierDate: string, laterDate: string): boolean => {
  const earlierDateFormatted = dayjs(earlierDate, 'YYYY-MM-DD').startOf('day');
  const laterDateFormatted = dayjs(laterDate, 'YYYY-MM-DD').startOf('day');
  if (!earlierDateFormatted.isValid() || !laterDateFormatted.isValid()) {
    return false;
  }
  return laterDateFormatted.isBefore(earlierDateFormatted);
};
const scrollableDateFormat = (date: any) => {
  if (!date) return null;
  const formattedDate = dayjs(date).format('MMM DD');
  return formattedDate;
};
const dataBaseFriendlyDateFormat = (date: string, type: string, timezone?: string) => {
  if (!date) return null;
  let base_date;
  if (type === 'startDate') {
    base_date = dayjs(date)
      .startOf('day')
      ?.format(`YYYY-MM-DDT${getTimeFormatAccordingToPreference()}:ss.000`);
  } else if (type === 'endDate') {
    base_date = dayjs(date)
      .endOf('day')
      ?.format(`YYYY-MM-DDT${getTimeFormatAccordingToPreference()}:ss.000`);
  } else {
    base_date = dayjs(date)?.format(`YYYY-MM-DDT${getTimeFormatAccordingToPreference()}:ss.000`);
  }
  base_date = dayjs(base_date)
    ?.format(`YYYY-MM-DDT${getTimeFormatAccordingToPreference()}:ss.000`)
    ?.concat(extractTimezoneOffset(timezone) || '+00:00');

  return base_date;
};
const extractTimezoneOffset = (timezoneStr: string | undefined): string | null => {
  if (!timezoneStr) return null;
  const pattern = /-?\d{1,2}:\d{2}/g;
  const matches = timezoneStr.match(pattern);
  if (matches && matches.length > 0) {
    const sign: string = matches[0]?.substring(0, 1);
    let timeOffset: any = matches[0]?.substring(1, matches[0]?.length);
    const [hoursString, minutesString] = timeOffset.split(':');
    const formattedHours = hoursString.length === 1 ? `0${hoursString}` : hoursString;
    return `${sign}${formattedHours}:${minutesString}`;
  }
  return null;
};

export const getNewDates = (startDate: any, endDate: any, type?: 'origin' | 'destination') => {
  const newStartDate = commonDateFormat(
    isBeforeCurrentDate(startDate) ? dayjs() : startDate,
  ) as string;
  let dateOffset = pickupDateOffset;
  if (type === 'destination') {
    dateOffset = dropDateOffset;
  }
  const newEndDate = commonDateFormat(
    isBeforeCurrentDate(endDate) ? dayjs().add(dateOffset, 'day') : endDate,
  ) as string;
  return { newStartDate, newEndDate };
};

export const getTimeFormatAccordingToPreference = (
  withAMPM = false,
  withHH = false,
  withSmallAMPM = false,
  withhh = false,
) => {
  return getCookie(COOKIE_IDS?.PREFERRED_TIME_FORMAT) === '24HRS FORMAT'
    ? 'HH:mm'
    : `${withHH ? 'HH' : withhh ? 'hh' : 'h'}:mm${withAMPM ? ` ${withSmallAMPM ? 'a' : 'A'}` : ''}`;
};

export const getFormatedDateTime = (createdDateTime: any) => {
  const actualStartDateTime =
    (createdDateTime && createdDateTime != '' && new Date(parseInt(createdDateTime))) ||
    createdDateTime;
  const monthName: string = actualStartDateTime.toLocaleString('en-US', { month: 'short' });
  const date: number = actualStartDateTime.getDate();
  const time: string = actualStartDateTime.toLocaleString('en-US', {
    timeZone: 'CST',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
  return `${monthName} ${date} ${time} Central`;
};

export {
  dayjs,
  utc,
  timezone,
  duration,
  commonDateFormat,
  appointmentDateFormat,
  appointmentTimeFormat,
  scrollableDateFormat,
  isBeforeCurrentDate,
  isBeforeCurrentDateAndTime,
  isBeforeFirstDate,
  dataBaseFriendlyDateFormat,
  convertToTimezoneFormat,
  convertToTimezoneFormatwithSmallhh,
};
